import FaceScreenDemo from "./FaceScreenDemo";

const SPECS = [
    { k: "COMPUTE", v: "Raspberry Pi" },
    { k: "DISPLAYS", v: "Face + Belly Touchscreen" },
    { k: "BRAIN", v: "Local Automation" },
    { k: "HOME CONTROL", v: "Home Assistant" },
    { k: "PERSONALITY", v: "Questionable", cls: "text-amber-600" },
    { k: "CLOUD DEPENDENCY", v: "Preferably None", cls: "text-cyan-600" },
    { k: "ARMS", v: "Lobster-Adjacent" },
];

export default function RoboticsSection() {
    return (
        <section id="robotics" className="py-20 border-b border-subtle bg-[#e9edf1]" data-testid="robotics-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
                    <div>
                        <span className="font-mono text-xs text-cyan-600 tracking-widest block uppercase">
                            // Timbotic Robotics Division // Project TR-001
                        </span>
                        <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">
                            ROBOT 001 // PHYSICAL SYSTEM
                        </h2>
                    </div>
                    <div className="flex items-center gap-3 font-mono text-xs">
                        <span className="px-3 py-1.5 rounded border border-amber-500/30 bg-amber-100 text-amber-700">
                            PROTOTYPE // ACTIVE
                        </span>
                        <span className="px-3 py-1.5 rounded border border-emerald-500/30 bg-emerald-100 text-emerald-700">
                            CLASSIFICATION: FRIENDLY MACHINE
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    <div className="lg:col-span-5 space-y-4">
                        <div
                            data-testid="robot-image"
                            className="rounded-lg border border-subtle bg-white relative overflow-hidden group"
                        >
                            <img
                                src="/robot/robot-final-render.png"
                                alt="Final render of Robot 001: red enclosure, expressive claws, face display and belly touchscreen"
                                loading="lazy"
                                className="w-full h-auto block"
                            />
                            <div className="border-t border-slate-200 px-4 py-2.5 font-mono text-[10px] flex items-center justify-between text-slate-500">
                                <span>FIG. 01 — FINAL RENDER // PRODUCTION INTENT</span>
                                <span className="text-red-600 font-bold">COFFEE ICON ON BELLY: NOTED</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                {
                                    src: "/robot/robot-elevation-views.png",
                                    fig: "FIG. 02 — ENGINEERING ELEVATIONS",
                                    note: "295×230MM // ARMS: LOBSTER-ADJACENT",
                                    alt: "Robot 001 approved design: front, side and rear elevations with dimensions",
                                    testid: "robot-figure-2",
                                },
                                {
                                    src: "/robot/robot-cad-dimensions.png",
                                    fig: "FIG. 03 — DIMENSIONED CAD",
                                    note: "5\" FACE + 10\" BELLY",
                                    alt: "Dimensioned CAD line drawing of Robot 001 front and side elevations",
                                    testid: "robot-figure-3",
                                },
                                {
                                    src: "/robot/robot-render.png",
                                    fig: "FIG. 04 — COLOUR STUDY",
                                    note: "PRE-LOBSTER CONCEPT",
                                    alt: "Early white render of Robot 001 with coloured control buttons",
                                    testid: "robot-figure-4",
                                },
                                {
                                    src: "/robot/robot-cad-model.png",
                                    fig: "FIG. 05 — HOLLOW BODY CAD",
                                    note: "PI MOUNT VISIBLE",
                                    alt: "CAD model of the hollow Robot 001 enclosure body",
                                    testid: "robot-figure-5",
                                },
                                {
                                    src: "/robot/robot-design-session.png",
                                    fig: "FIG. 06 — ITERATION LOG",
                                    note: "AI-ASSISTED. SUPERVISED.",
                                    alt: "Design iteration session showing Robot 001 concept refinement",
                                    testid: "robot-figure-6",
                                    wide: true,
                                },
                            ].map((f) => (
                                <figure
                                    key={f.fig}
                                    data-testid={f.testid}
                                    className={`rounded border border-subtle bg-white overflow-hidden group ${
                                        f.wide ? "col-span-2" : ""
                                    }`}
                                >
                                    <div className={`${f.wide ? "aspect-[21/9]" : "aspect-[4/3]"} overflow-hidden bg-white`}>
                                        <img
                                            src={f.src}
                                            alt={f.alt}
                                            loading="lazy"
                                            className="h-full w-full object-contain group-hover:scale-[1.03] transition-transform duration-500"
                                        />
                                    </div>
                                    <figcaption className="border-t border-slate-200 px-3 py-2 font-mono text-[9px] text-slate-500 flex flex-col">
                                        <span>{f.fig}</span>
                                        <span className="text-cyan-600 mt-0.5">{f.note}</span>
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-7 space-y-6">
                        <p className="text-slate-600 text-base leading-relaxed border-l-2 border-cyan-500/40 pl-5">
                            &ldquo;A small local-first robot designed to notice what&rsquo;s happening around the
                            home and occasionally be useful without becoming unbearably annoying.&rdquo;
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 font-mono text-xs">
                            {SPECS.map((s) => (
                                <div key={s.k} className="p-3.5 rounded bg-white border border-subtle">
                                    <span className="text-slate-500 block text-[10px]">{s.k}</span>
                                    <span className={`font-semibold ${s.cls || "text-slate-700"}`}>{s.v}</span>
                                </div>
                            ))}
                            <div className="p-3.5 rounded bg-white border border-dashed border-slate-300">
                                <span className="text-slate-600 block text-[10px]">FIELD NOTES</span>
                                <span className="text-slate-500 font-semibold italic">Robot declined to comment</span>
                            </div>
                        </div>

                        <FaceScreenDemo />
                    </div>
                </div>
            </div>
        </section>
    );
}
